/**
 * O carreiro de saibro que liga a porta sul da Recepção ao parque de mobile homes.
 *
 * A primeira versão resolvia a orla DENTRO da textura — os quatro cantos ficavam
 * por pintar e a relva de baixo aparecia lá. Poupava a varredura de vizinhança,
 * mas lia-se exactamente como o que era: um rectângulo com uma dentadinha de
 * 2 px em cada esquina. Ao vivo, com o tile a repetir-se, o olho só via a linha
 * recta de 32 em 32 pixels.
 *
 * Agora a orla é POR LADO e vem de fora: o cliente diz quais dos quatro vizinhos
 * são relva (`pathMask`) e o tile recorta-se só desses lados, com um perfil
 * irregular de 0 a 3 px que continua de tile para tile — as pontas dos perfis
 * valem todas 1, portanto a junta nunca dá degrau. Onde dois lados recortados se
 * encontram tira-se ainda um quarto de disco: é isso que faz a esquina do
 * carreiro ser redonda em vez de um canto a 90°. Um tile rodeado de carreiro
 * (`mask === 0`) fica cheio de lado a lado — no meio do caminho não há orla
 * nenhuma para desenhar.
 *
 * São 16 recortes × 2 variantes = 32 texturas de 16×16, geradas uma vez no
 * arranque. O traçado continua a ser do `space.ts` (`GARDEN_PATH`).
 */

export interface Brush {
  r(x: number, y: number, w: number, h: number, color: string): void;
  dither(x: number, y: number, w: number, h: number, a: string, b: string): void;
}

/** Tamanho da arte de um tile de carreiro (o cliente escala-o para os 32 px do TILE). */
export const PATH_ART = { w: 16, h: 16 } as const;

/** Os quatro lados, em bits. Ligado = daquele lado é relva, logo há orla a fazer. */
export const EDGE = { N: 1, E: 2, S: 4, W: 8 } as const;

/** Quantos recortes distintos existem — 2^4. O cliente gera uma textura por cada. */
export const EDGE_COUNT = 16;

/**
 * O recorte deste tile: um bit por vizinho que NÃO é carreiro.
 *
 * Vive aqui e não no cliente porque é a metade lógica do desenho — quem mexer
 * nos perfis de orla tem de poder ver, no mesmo ficheiro, o que é que os liga ao
 * mapa. `caminho` é o `onGardenPath` do `space.ts`, passado como função para este
 * módulo não depender do pacote partilhado.
 */
export function pathMask(caminho: (col: number, row: number) => boolean, col: number, row: number): number {
  let m = 0;
  if (!caminho(col, row - 1)) m |= EDGE.N;
  if (!caminho(col + 1, row)) m |= EDGE.E;
  if (!caminho(col, row + 1)) m |= EDGE.S;
  if (!caminho(col - 1, row)) m |= EDGE.W;
  return m;
}

/**
 * Quanto é que cada lado se come, pixel a pixel ao longo dele.
 *
 * Duas regras, e as duas custaram uma versão a aprender:
 *
 * 1. TODOS começam e acabam em 1. Dois tiles seguidos usam perfis diferentes (o
 *    xadrez alterna a variante), e é essa igualdade nas pontas que impede um
 *    degrau de 3 px na junta — que de longe se lia como uma serra regular.
 * 2. O valor nunca salta mais do que 1 de um pixel para o seguinte. A primeira
 *    versão oscilava a cada pixel (1,2,2,1,0,1,2,3…) e o resultado não era uma
 *    borda ondulada — era uma borda ROÍDA, dentes de 3 px alternados com nada.
 *    Pior: enchia o vértice de buracos, e o arco de esquina (a seguir) deixava
 *    de ter onde morder, ficando decorativo. Uma onda lenta de 2 a 3 lóbulos por
 *    tile dá 12 a 20 px de ecrã por lóbulo — isso sim lê-se como margem gasta.
 */
const PERFIS: readonly (readonly number[])[] = [
  [1, 1, 2, 2, 3, 3, 2, 2, 1, 1, 0, 0, 1, 1, 2, 1],
  [1, 2, 3, 3, 2, 1, 1, 0, 0, 1, 2, 2, 3, 3, 2, 1],
  [1, 0, 0, 1, 2, 3, 3, 2, 1, 1, 2, 3, 2, 1, 1, 1],
  [1, 2, 2, 1, 1, 0, 1, 2, 3, 3, 2, 1, 0, 0, 1, 1],
];

/** Que perfil vai a cada lado (N,E,S,W), por variante — trocados para o xadrez não rimar. */
const LADOS: readonly (readonly [number, number, number, number])[] = [
  [0, 1, 2, 3],
  [2, 3, 0, 1],
];

/**
 * Raio do quarto de disco que arredonda uma esquina exterior do carreiro.
 *
 * Tem de ser bem maior do que a profundidade dos perfis (3 px), senão o arco cai
 * INTEIRO dentro do que os dois perfis já comeram e não arredonda coisa nenhuma —
 * mediu-se: a 5 px tirava exactamente 0 pixels a mais do que os dois lados juntos,
 * a esquina era só o cruzamento de dois cortes. A 9 px o arco volta a morder.
 *
 * O que ele garante não é a média (são 2 a 3 px) — é o pior caso: sem ele, uma
 * esquina onde calhassem os dois perfis no seu mínimo ficava um canto a 90° em
 * cima do carreiro todo ondulado. Com ele, o vértice nunca sobrevive.
 */
const RAIO_CANTO = 9;

/** Este pixel cai fora do quarto de disco daquela esquina? (canto: 0=NO 1=NE 2=SE 3=SO) */
function foraDoArco(x: number, y: number, canto: number): boolean {
  const direita = canto === 1 || canto === 2;
  const baixo = canto === 2 || canto === 3;
  const cx = direita ? PATH_ART.w - RAIO_CANTO - 0.5 : RAIO_CANTO - 0.5;
  const cy = baixo ? PATH_ART.h - RAIO_CANTO - 0.5 : RAIO_CANTO - 0.5;
  const dx = x - cx;
  const dy = y - cy;
  if (direita ? dx <= 0 : dx >= 0) return false;
  if (baixo ? dy <= 0 : dy >= 0) return false;
  return dx * dx + dy * dy > RAIO_CANTO * RAIO_CANTO;
}

/** A silhueta do tile: 1 = pinta-se, 0 = fica a relva de baixo a ver-se. */
function silhueta(alt: boolean, mask: number): Uint8Array {
  const m = new Uint8Array(PATH_ART.w * PATH_ART.h).fill(1);
  const [iN, iE, iS, iW] = LADOS[alt ? 1 : 0] ?? [0, 1, 2, 3];
  const dN = PERFIS[iN] ?? [];
  const dE = PERFIS[iE] ?? [];
  const dS = PERFIS[iS] ?? [];
  const dW = PERFIS[iW] ?? [];
  const norte = (mask & EDGE.N) !== 0;
  const este = (mask & EDGE.E) !== 0;
  const sul = (mask & EDGE.S) !== 0;
  const oeste = (mask & EDGE.W) !== 0;
  for (let y = 0; y < PATH_ART.h; y += 1) {
    for (let x = 0; x < PATH_ART.w; x += 1) {
      let corta = false;
      if (norte && y < (dN[x] ?? 0)) corta = true;
      if (sul && y >= PATH_ART.h - (dS[x] ?? 0)) corta = true;
      if (oeste && x < (dW[y] ?? 0)) corta = true;
      if (este && x >= PATH_ART.w - (dE[y] ?? 0)) corta = true;
      // As esquinas exteriores: onde dois lados recortados se encontram, é o arco
      // que manda. Sem ele o cruzamento dos dois perfis dá uma escadinha e o
      // carreiro volta a ter cantos — que foi a queixa toda.
      if (!corta) {
        if (norte && oeste && foraDoArco(x, y, 0)) corta = true;
        else if (norte && este && foraDoArco(x, y, 1)) corta = true;
        else if (sul && este && foraDoArco(x, y, 2)) corta = true;
        else if (sul && oeste && foraDoArco(x, y, 3)) corta = true;
      }
      if (corta) m[y * PATH_ART.w + x] = 0;
    }
  }
  return m;
}

/**
 * Um pincel que só deixa passar o que cai dentro da silhueta.
 *
 * Sem isto, cada pedra e cada grão teria de saber onde é que a orla o corta —
 * multiplicado por 16 recortes. Assim o desenho é escrito uma vez, como se o
 * tile fosse cheio, e a silhueta trata da tesoura. Os rectângulos saem em tiras
 * horizontais contíguas para o pincel real não levar 256 chamadas por tile.
 */
function tesoura(b: Brush, m: Uint8Array): Brush {
  const dentro = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < PATH_ART.w && y < PATH_ART.h && m[y * PATH_ART.w + x] === 1;
  const tiras = (x: number, y: number, w: number, h: number, cor: (px: number, py: number) => string): void => {
    for (let j = 0; j < h; j += 1) {
      const py = y + j;
      let ini = -1;
      let corAtual = "";
      for (let i = 0; i <= w; i += 1) {
        const pxl = x + i;
        const ok = i < w && dentro(pxl, py);
        const c = ok ? cor(pxl, py) : "";
        if (ok && (ini < 0 || c !== corAtual)) {
          if (ini >= 0) b.r(ini, py, pxl - ini, 1, corAtual);
          ini = pxl;
          corAtual = c;
        } else if (!ok && ini >= 0) {
          b.r(ini, py, pxl - ini, 1, corAtual);
          ini = -1;
        }
      }
    }
  };
  return {
    r: (x, y, w, h, color) => tiras(x, y, w, h, () => color),
    // O xadrez sai das coordenadas do TILE, não do rectângulo: assim dois
    // rectângulos encostados não deixam costura na areia.
    dither: (x, y, w, h, a, c) => tiras(x, y, w, h, (px, py) => ((px + py) & 1 ? a : c)),
  };
}

/** Multiplica um `#rrggbb` por um factor, com clamp — cópia local da do `officeArt`. */
function shade(hex: string, f: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const ch = (v: number): number => Math.max(0, Math.min(255, Math.round(v * f)));
  const r = ch((n >> 16) & 0xff);
  const g = ch((n >> 8) & 0xff);
  const b = ch(n & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/** Lajes assentes no leito: x, y, largura, altura. */
const PEDRAS: readonly (readonly (readonly [number, number, number, number])[])[] = [
  [
    [1, 2, 4, 3],
    [7, 1, 5, 3],
    [2, 8, 4, 3],
    [8, 7, 3, 2],
    [11, 11, 4, 3],
    [4, 12, 5, 3],
  ],
  [
    [2, 1, 5, 3],
    [9, 3, 4, 3],
    [1, 6, 3, 3],
    [6, 8, 5, 3],
    [12, 7, 3, 3],
    [3, 12, 5, 3],
  ],
];

/** Seixos: a pedrinha miúda que se pisa, com um pixel de sombra por baixo. */
const SEIXOS: readonly (readonly (readonly [number, number, number, number])[])[] = [
  [
    [3, 4, 2, 1],
    [8, 6, 2, 2],
    [12, 5, 1, 2],
    [6, 11, 2, 1],
    [10, 14, 2, 1],
    [1, 13, 2, 2],
    [14, 10, 1, 2],
    [5, 2, 2, 1],
    [9, 10, 2, 1],
    [13, 2, 2, 1],
  ],
  [
    [2, 3, 2, 2],
    [7, 2, 2, 1],
    [11, 5, 1, 2],
    [4, 10, 2, 1],
    [8, 12, 2, 2],
    [13, 9, 2, 1],
    [1, 10, 1, 2],
    [10, 6, 2, 1],
    [5, 14, 2, 1],
    [14, 3, 2, 1],
  ],
];

/**
 * Musgo. Mora de propósito encostado às bordas: é lá que a relva faz sombra e o
 * chão se mantém húmido, e é lá que ele mais serve — a mancha verde por cima do
 * recorte irregular é o que apaga de vez a fronteira entre pedra e relva.
 */
const MUSGO: readonly (readonly (readonly [number, number, number, number])[])[] = [
  [
    [0, 5, 3, 2],
    [13, 3, 2, 2],
    [5, 14, 4, 1],
    [9, 0, 3, 1],
    [2, 10, 2, 2],
    [11, 9, 2, 1],
  ],
  [
    [0, 9, 2, 3],
    [14, 7, 2, 2],
    [3, 0, 3, 1],
    [8, 14, 4, 1],
    [6, 5, 2, 2],
    [12, 12, 2, 1],
  ],
];

/**
 * Grão fino, espalhado por hash em vez de por tabela: são 28 pixels por variante
 * e uma lista deles a seguir às outras três só faria ruído no ficheiro. O hash é
 * determinístico, portanto a textura é sempre a mesma — o que importa aqui.
 */
function ruido(i: number, alt: boolean): number {
  let n = (Math.imul(i + 1, 0x9e3779b1) + (alt ? 0x85ebca6b : 0xc2b2ae35)) >>> 0;
  n ^= n >>> 15;
  n = Math.imul(n, 0x2545f491) >>> 0;
  n ^= n >>> 13;
  return n >>> 0;
}

/** Um tile de carreiro: leito de saibro, lajes, seixos, musgo e grão, recortado pela orla. */
export function drawGravel(brush: Brush, alt: boolean, mask: number): void {
  const b = tesoura(brush, silhueta(alt, mask));
  const base = alt ? "#948b7e" : "#8c8377";
  const dk = shade(base, 0.78);
  const areia = shade(base, 1.09);
  const hi = shade(base, 1.22);

  // Leito: chapa de saibro com areia salpicada por cima.
  b.r(0, 0, PATH_ART.w, PATH_ART.h, base);
  b.dither(0, 0, PATH_ART.w, PATH_ART.h, areia, base);

  // Lajes, com aresta iluminada em cima e a sombra a assentar no leito.
  const pedras = PEDRAS[alt ? 1 : 0] ?? [];
  for (let i = 0; i < pedras.length; i += 1) {
    const p = pedras[i];
    if (!p) continue;
    const [x, y, w, h] = p;
    const corpo = shade(base, i % 3 === 0 ? 1.14 : i % 3 === 1 ? 0.97 : 1.05);
    b.r(x, y, w, h, corpo);
    b.r(x, y, w, 1, shade(corpo, 1.16));
    b.r(x, y + h - 1, w, 1, shade(corpo, 0.74));
    b.r(x, y, 1, h, shade(corpo, 1.06));
  }

  // Seixos: corpo claro, sombra fina em baixo. É o que dá a leitura de pedrisco
  // solto por cima da terra batida em vez de terra lisa.
  for (const s of SEIXOS[alt ? 1 : 0] ?? []) {
    const [x, y, w, h] = s;
    b.r(x, y + h, w, 1, dk);
    b.r(x, y, w, h, hi);
    b.r(x, y, 1, 1, shade(base, 1.34));
  }

  // Musgo por cima de tudo — cresce na junta e sobe pela pedra, não fica por baixo.
  // Os dois tons têm de ficar MAIS ESCUROS do que a relva à volta (`#448241`,
  // luma 104): musgo mais claro do que o relvado lê-se como remendo de erva boa a
  // meio da pedra, e não como sombra húmida na junta. O claro daqui vale 93.
  const musgoDk = "#4e5a30";
  const musgoHi = "#5a6638";
  for (const t of MUSGO[alt ? 1 : 0] ?? []) {
    const [x, y, w, h] = t;
    b.r(x, y, w, h, musgoDk);
    b.r(x, y, Math.max(1, w - 1), 1, musgoHi);
  }

  for (let i = 0; i < 28; i += 1) {
    const n = ruido(i, alt);
    b.r(n % PATH_ART.w, ((n >>> 8) % PATH_ART.h) | 0, 1, 1, (n >>> 16) & 1 ? hi : dk);
  }
}
